/**
 * Display helpers for people and their stored relations.
 *
 * Everything user-visible here is an i18n key, per docs/08 §11 — including the
 * enum vocabularies, which M6 has to translate alongside the kinship labels.
 * Nothing in this file derives kinship: these are the *stored* facts, spelled out.
 */

import type { TFunction } from 'i18next';
import {
  formatLifespan,
  type Person,
  type PersonSummary,
  type RelationAssertion,
} from '@famtree/shared';

/**
 * What to show for a person — always the server's `display_label`.
 *
 * The api resolves this once: the written name where there is one, and for
 * somebody the family cannot name, the relation that places them ("mother of
 * Hassan"). Concatenating the name columns here instead would put a blank row on
 * screen for exactly the people who most need a handle.
 */
export function personName(person: Pick<PersonSummary, 'display_label'>): string {
  return person.display_label;
}

/** "1980–2001", "~1943–", or null when neither end is recorded. */
export function lifespan(person: Pick<PersonSummary, 'birth' | 'death'>): string | null {
  return formatLifespan(person.birth, person.death);
}

/**
 * One line describing what a relation *says*, in the vocabulary of the row
 * itself: "mother (step)", "marriage 2001 – 2010 (divorce)", "full sibling".
 */
export function relationDescription(relation: RelationAssertion, t: TFunction): string {
  const parts: string[] = [];

  if (relation.kind === 'parent_child') {
    parts.push(t(`enums.parent_role.${relation.parent_role ?? 'parent'}`));
    if (relation.parent_basis) parts.push(`(${t(`enums.parent_basis.${relation.parent_basis}`)})`);
  } else if (relation.kind === 'union') {
    parts.push(t(`enums.union_type.${relation.union_type ?? 'unknown'}`));
  } else {
    parts.push(t(`enums.sibling_basis.${relation.sibling_basis ?? 'unknown'}`));
    parts.push(t('people.sibling'));
  }

  const span = formatLifespan(relation.started, relation.ended);
  if (span) parts.push(span);
  if (relation.end_reason) parts.push(`(${t(`enums.union_end_reason.${relation.end_reason}`)})`);

  return parts.join(' ');
}

/**
 * The default `parent_role` suggested for a parent, from their gender — a
 * suggestion only: role is the social/registered role and is freely overridable
 * (docs/02 §2.2).
 */
export function suggestedParentRole(
  gender: PersonSummary['gender'],
): 'mother' | 'father' | 'parent' {
  if (gender === 'female') return 'mother';
  if (gender === 'male') return 'father';
  return 'parent';
}

/**
 * The `PersonSummary` view of a full `Person`.
 *
 * Endpoints that return the whole person (the profile, a create result) hand
 * back a superset of the summary every picker and chip renders; narrowing it in
 * one place keeps the two shapes from drifting apart field by field.
 */
export function toPersonSummary(person: Person): PersonSummary {
  return {
    id: person.id,
    display_label: person.display_label,
    given_names: person.given_names,
    family_name: person.family_name,
    nickname: person.nickname,
    gender: person.gender,
    living_status: person.living_status,
    birth: person.birth,
    death: person.death,
    status: person.status,
    portrait_photo_id: person.portrait_photo_id,
  };
}
